<script>
  import MetricCard from '../components/admin/MetricCard.svelte';
  import BarChart from '../components/admin/BarChart.svelte';
  import DataTable from '../components/admin/DataTable.svelte';
  import LineChart from '../components/admin/LineChart.svelte';
  import DonutChart from '../components/admin/DonutChart.svelte';
  import StackedBarChart from '../components/admin/StackedBarChart.svelte';
  import WorldMap from '../components/admin/WorldMap.svelte';

  const metrics = [
    { title: 'Unique Visitors', value: '24.7K', change: '+20%', changeType: 'positive', subtitle: 'vs last week' },
    { title: 'Event Registrations', value: '55.9K', change: '+4%', changeType: 'positive', subtitle: 'vs last week' },
    { title: 'Bounce Rate', value: '54%', change: '-18%', changeType: 'negative', subtitle: 'vs last week' },
    { title: 'Visit Duration', value: '2m 56s', change: '+7%', changeType: 'positive', subtitle: 'vs last week' }
  ];

  const analyticsData = [520, 320, 450, 380, 520, 690, 420, 510, 380, 470, 550, 420, 680, 510, 420, 380, 550, 690, 420, 510, 380, 470, 580, 420];
  const analyticsLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const topChannelsColumns = [
    { key: 'source', label: 'Source' },
    { key: 'visitors', label: 'Visitors' },
    { key: 'revenues', label: 'Revenues' },
    { key: 'sales', label: 'Sales' },
    { key: 'conversion', label: 'Conversion' }
  ];

  const topChannelsData = [
    { source: 'Google', visitors: '3.5K', revenues: '$5,768', sales: 590, conversion: '4.8%' },
    { source: 'Github', visitors: '2.2K', revenues: '$4,635', sales: 467, conversion: '4.3%' },
    { source: 'Gitlab', visitors: '2.1K', revenues: '$4,290', sales: 420, conversion: '3.7%' },
    { source: 'Facebook', visitors: '1.5K', revenues: '$3,580', sales: 389, conversion: '2.5%' }
  ];

  const topPagesColumns = [
    { key: 'page', label: 'Top Pages' },
    { key: 'visitors', label: 'Visitors' },
    { key: 'unique', label: 'Unique Page Visits' },
    { key: 'bounce', label: 'Bounce Rate' }
  ];

  const topPagesData = [
    { page: '/dashboard/analytics', visitors: '3.2K', unique: '2.1K', bounce: '23%' },
    { page: '/products/introduction', visitors: '2.7K', unique: '1.9K', bounce: '31%' },
    { page: '/docs/installation/guide', visitors: '2.3K', unique: '1.7K', bounce: '28%' },
    { page: '/about-us/about-company', visitors: '1.9K', unique: '1.4K', bounce: '35%' }
  ];

  const activeUsersData = [280, 310, 290, 330, 350, 370, 340, 360, 380, 350, 340, 360];
  const activeUsersLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const activeUsersStats = [
    { value: '22k', label: 'Daily Avg.' },
    { value: '1.4K', label: 'Weekly Avg.' },
    { value: '22.1K', label: 'Monthly Avg.' }
  ];

  const acquisitionDatasets = [
    { label: 'Organic', data: [30, 45, 40, 50, 35, 45, 40, 50], color: '#3B82F6' },
    { label: 'Referral', data: [25, 30, 35, 30, 40, 35, 45, 40], color: '#60A5FA' },
    { label: 'Email', data: [20, 25, 20, 30, 25, 30, 25, 30], color: '#93C5FD' }
  ];
  const acquisitionLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];

  const deviceData = [65, 25, 10];
  const deviceLabels = ['Desktop', 'Mobile', 'Tablet'];
  const deviceColors = ['#3B82F6', '#60A5FA', '#93C5FD'];

  const countries = [
    { name: 'USA', flag: '🇺🇸', percentage: 70 },
    { name: 'France', flag: '🇫🇷', percentage: 20 },
    { name: 'Italy', flag: '🇮🇹', percentage: 10 }
  ];

  const recentOrdersColumns = [
    { key: 'product', label: 'Products' },
    { key: 'category', label: 'Category' },
    { key: 'customer', label: 'Customer' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Amount' }
  ];

  const recentOrdersData = [
    { product: 'MacBook Pro', category: 'Electronics', customer: 'USAID', status: 'Delivered', amount: '$2,999' },
    { product: 'iPhone 14 Pro', category: 'Electronics', customer: 'Salesforce', status: 'Delivered', amount: '$1,299' },
    { product: 'Headphones', category: 'Electronics', customer: 'Slack', status: 'Delivered', amount: '$349' },
    { product: 'Nike Shoes', category: 'Fashion', customer: 'Spotify', status: 'Delivered', amount: '$199' },
    { product: 'Travel Bag', category: 'Fashion', customer: 'Stripe', status: 'Delivered', amount: '$89' },
    { product: 'AirPods Pro', category: 'Electronics', customer: 'Figma', status: 'Cancelled', amount: '$249' }
  ];
</script>

<div class="min-h-screen bg-gray-50 p-6">
  <div class="max-w-7xl mx-auto">
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
      <p class="text-gray-500 mt-1">Track your business metrics and performance</p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
      {#each metrics as metric}
        <MetricCard {...metric} />
      {/each}
    </div>

    <div class="mb-6">
      <BarChart title="Analytics" subtitle="Website Analytics over time" data={analyticsData} labels={analyticsLabels} height={350} />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
      <DataTable title="Top Channels" columns={topChannelsColumns} data={topChannelsData} showViewAll={true} />
      <DataTable title="Top Pages" columns={topPagesColumns} data={topPagesData} showViewAll={true} />
      <LineChart title="Active Users" value="352" data={activeUsersData} labels={activeUsersLabels} stats={activeUsersStats} height={180} />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <StackedBarChart title="Acquisition Channels" datasets={acquisitionDatasets} labels={acquisitionLabels} height={300} />
      <DonutChart title="Sessions by Device" data={deviceData} labels={deviceLabels} colors={deviceColors} />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <WorldMap title="Customer Demographic" countries={countries} />
      <DataTable title="Recent Orders" columns={recentOrdersColumns} data={recentOrdersData} showViewAll={true} />
    </div>
  </div>
</div>
